import axios from 'axios';
import CryptoJS from 'crypto-js';
import queryString from 'query-string';
import { AbcLoginResult, refreshToken } from './login';
import { z } from 'zod';
import { createSecureChannel } from './securechannel';
import env from '../../env';
import { GetWalletResult } from './mpc';

const API_URL = `${env.REACT_APP_API_SERVER}/api/waas`;

export interface SendTxParams {
  network: string;
  from?: string;
  to?: string;
  gasLimit?: string;
  value?: string;
  data?: string;
  gasPrice?: string;
  nonce?: string;
  maxPriorityFeePerGas?: string;
  maxFeePerGas?: string;
}

export interface SignParams {
  network: string;
  encryptDevicePassword: string;
  pvencstr: string;
  uid: string;
  wid: string;
  sid: string;
  message?: string;
  to?: string;
  from?: string;
  value?: string;
  data?: string;
  nonce?: string;
  gasPrice?: string;
  gasLimit?: string;
  maxPriorityFeePerGas?: string;
  maxFeePerGas?: string;
  type: string;
}

export interface HashSignParams {
  encryptDevicePassword: string;
  pvencstr: string;
  uid: string;
  wid: string;
  sid: string;
  hash: string;
}

export interface WalletsParams {
  email: string;
  devicePassword: string;
}

export const TxResultResponse = z.object({
  result: z.string(),
});

export interface TxReceipt {
  blockHash: string;
  blockNumber: string;
  contractAddress: string;
  cumulativeGasUsed: string;
  effectiveGasPrice: string;
  from: string;
  gasUsed: string;
  logs: {
    address: string;
    blockHash: string;
    blockNumber: string;
    data: string;
    logIndex: string;
    removed: string;
    topics: string[];
    transactionHash: string;
    transactionIndex: string;
  };
  logsBloom: string;
  status: string;
  to: string;
  transactionHash: string;
  transactionIndex: string;
  type: string;
}

export const SignResultResponse = z.object({
  serializedTx: z.string(),
  rawTx: z.string(),
});

export const HashSignResultResponse = z.object({
  signstr: z.string(),
  iserr: z.boolean(),
  errmsg: z.string(),
});

export const WalletsResultResponse = z.object({
  uid: z.string(),
  wid: z.number(),
  sid: z.string(),
  pvencstr: z.string(),
  encryptDevicePassword: z.string(),
});

export interface LegacyResult {
  result: string;
}

export interface Eip1559Result {
  low: {
    suggestedMaxPriorityFeePerGas: string;
    suggestedMaxFeePerGas: string;
    minWaitTimeEstimate: number;
    maxWaitTimeEstimate: number;
  };
  medium: {
    suggestedMaxPriorityFeePerGas: string;
    suggestedMaxFeePerGas: string;
    minWaitTimeEstimate: number;
    maxWaitTimeEstimate: number;
  };
  high: {
    suggestedMaxPriorityFeePerGas: string;
    suggestedMaxFeePerGas: string;
    minWaitTimeEstimate: number;
    maxWaitTimeEstimate: number;
  };
  estimatedBaseFee: string;
  networkCongestion: string;
  latestPriorityFeeRange: string[];
  historicalPriorityFeeRange: string[];
  historicalBaseFeeRange: string[];
  priorityFeeTrend: string;
  baseFeeTrend: string;
}

export const sendTx = async (
  param: SendTxParams,
  type = 'EIP1559'
): Promise<string> => {
  try {
    const abcAuth: AbcLoginResult = JSON.parse(
      window.sessionStorage.getItem('abcAuth') || '{}'
    );

    const res = await axios.request({
      url: `${API_URL}/eth-call`,
      method: 'post',
      headers: {
        'content-type': 'application/x-www-form-urlencoded',
      },
      data: queryString.stringify({
        ...param,
        token: abcAuth.accessToken,
        type,
      }),
    });
    if (res.status !== 200) {
      throw new Error();
    }

    const resData = TxResultResponse.parse(res.data);
    return resData.result;
  } catch (error: any) {
    throw error?.response?.data;
  }
};

export const receipt = async (
  network: string,
  hash: string
): Promise<string> => {
  try {
    const abcAuth: AbcLoginResult = JSON.parse(
      window.sessionStorage.getItem('abcAuth') || '{}'
    );

    const res = await axios.request({
      url: `${API_URL}/transaction/receipt`,
      method: 'post',
      headers: {
        'content-type': 'application/x-www-form-urlencoded',
      },
      data: queryString.stringify({
        token: abcAuth.accessToken,
        network,
        hash,
      }),
    });
    if (res.status !== 200) {
      throw new Error();
    }
    console.log(res.data);

    const resData: TxReceipt = res.data;
    return resData.status;
  } catch (error: any) {
    throw error?.response?.data;
  }
};

export const sign = async (
  param: SignParams,
  secureChannel: string
): Promise<string> => {
  try {
    const abcAuth: AbcLoginResult = JSON.parse(
      window.sessionStorage.getItem('abcAuth') || '{}'
    );

    const res = await axios.request({
      url: `${API_URL}/sign`,
      method: 'post',
      headers: {
        'content-type': 'application/x-www-form-urlencoded',
      },
      data: queryString.stringify({
        ...param,
        token: abcAuth.accessToken,
        secureChannel,
      }),
    });
    if (res.status !== 200) {
      throw new Error();
    }
    if (res.data.code === '752') {
      // TODO : Refresh Secure Channel
      await createSecureChannel();
      // alert(`Secure Channel을 갱신했습니다. 다시 시도해 주세요.`);
      console.log(`Secure Channel을 갱신했습니다. 다시 시도해 주세요.`);
      // TODO : Auto Logout
      // window.sessionStorage.clear();
      // window.localStorage.removeItem('login');
      // window.localStorage.removeItem('wallet');
      // window.localStorage.setItem('walletStatus', 'disconnected');
      // window.localStorage.removeItem('nftapiJwtToken');
      // window.location.href = '/';
      // alert('Token is expired. 다시 로그인 주세요.');
      throw new Error(res.data.message);
    }

    const resData = SignResultResponse.parse(res.data);
    return resData.serializedTx;
  } catch (error: any) {
    throw error?.response?.data;
  }
};

export const hashSign = async (
  param: HashSignParams,
  secureChannel: string
): Promise<string> => {
  try {
    const abcAuth: AbcLoginResult = JSON.parse(
      window.sessionStorage.getItem('abcAuth') || '{}'
    );

    const res = await axios.request({
      url: `${API_URL}/hashsign`,
      method: 'post',
      headers: {
        'content-type': 'application/x-www-form-urlencoded',
      },
      data: queryString.stringify({
        ...param,
        token: abcAuth.accessToken,
        secureChannel,
      }),
    });
    if (res.status !== 200) {
      throw new Error();
    }

    const resData = HashSignResultResponse.parse(res.data);
    return resData.signstr;
  } catch (error: any) {
    throw error?.response?.data;
  }
};

export const feePayerSign = async (
  param: { hash: string },
  secureChannel: string
): Promise<string> => {
  try {
    const abcAuth: AbcLoginResult = JSON.parse(
      window.sessionStorage.getItem('abcAuth') || '{}'
    );

    const res = await axios.request({
      url: `${API_URL}/feepayersign`,
      method: 'post',
      headers: {
        'content-type': 'application/x-www-form-urlencoded',
      },
      data: queryString.stringify({
        ...param,
        token: abcAuth.accessToken,
        secureChannel,
      }),
    });
    if (res.status !== 200) {
      throw new Error();
    }

    const resData = HashSignResultResponse.parse(res.data);
    return resData.signstr;
  } catch (error: any) {
    throw error?.response?.data;
  }
};

export const wallets = async (
  param: WalletsParams,
  secureChannel: string
): Promise<GetWalletResult> => {
  try {
    const abcAuth: AbcLoginResult = JSON.parse(
      window.sessionStorage.getItem('abcAuth') || '{}'
    );

    const res = await axios.request({
      url: `${API_URL}/wallets`,
      method: 'post',
      headers: {
        'content-type': 'application/x-www-form-urlencoded',
      },
      data: queryString.stringify({
        ...param,
        token: abcAuth.accessToken,
        secureChannel,
      }),
    });
    if (res.status !== 200) {
      throw new Error();
    }

    return WalletsResultResponse.parse(res.data);
  } catch (error: any) {
    throw error?.response?.data;
  }
};

interface SecureChannel {
  ChannelID: string;
  Encrypted: string;
  Message: string;
  PrivateKey: string;
  SecretKey: string;
  ServerPublicKey: string;
}

export const encrypt = async (plain: string) => {
  const secureChannel: SecureChannel = JSON.parse(
    window.sessionStorage.getItem('secureChannel') || '{}'
  );
  const secretKey = secureChannel.SecretKey;
  const key = secretKey.substring(0, 32);
  const iv = secretKey.substring(32);
  const encrypted = CryptoJS.AES.encrypt(plain, CryptoJS.enc.Hex.parse(key), {
    iv: CryptoJS.enc.Hex.parse(iv),
    padding: CryptoJS.pad.Pkcs7,
    mode: CryptoJS.mode.CBC,
  });
  // @ts-ignore
  return {
    encryptPlain: encrypted.toString(),
    secureChannel: secureChannel.ChannelID,
  };
};

export const getEncryptPlain = async (plain: string) => {
  const res = await encrypt(plain);
  return res.encryptPlain;
};

export const getGasData = async (
  network: string,
  type: string
): Promise<any> => {
  try {
    const abcAuth: AbcLoginResult = JSON.parse(
      window.sessionStorage.getItem('abcAuth') || '{}'
    );

    const res = await axios.request({
      url: `${API_URL}/gasData`,
      method: 'post',
      headers: {
        'content-type': 'application/x-www-form-urlencoded',
      },
      data: queryString.stringify({
        token: abcAuth.accessToken,
        network,
        type,
      }),
    });
    if (res.status !== 200) {
      throw new Error();
    }

    let restData = res.data;
    if (
      restData &&
      restData.message &&
      restData.message.includes('The token has expired.')
    ) {
      const res = await refreshToken();
      const accessToken = res?.accessToken;

      if (
        accessToken !== '' &&
        accessToken !== null &&
        accessToken !== undefined
      ) {
        const res = await axios.request({
          url: `${API_URL}/gasData`,
          method: 'post',
          headers: {
            'content-type': 'application/x-www-form-urlencoded',
          },
          data: queryString.stringify({
            token: accessToken,
            network,
            type,
          }),
        });
        if (res.status !== 200) {
          throw new Error();
        }

        restData = res.data;
      } else {
        console.log('refreshToken is expired. Re-Login is required!');
        alert('refreshToken is expired. Re-Login is required!');
      }
    }
    return restData;
  } catch (error: any) {
    throw error?.response?.data;
  }
};

export const estimate = async (
  param: SendTxParams,
  type = 'EIP1559'
): Promise<string | undefined> => {
  try {
    const abcAuth: AbcLoginResult = JSON.parse(
      window.sessionStorage.getItem('abcAuth') || '{}'
    );

    const res = await axios.request({
      url: `${API_URL}/estimate`,
      method: 'post',
      headers: {
        'content-type': 'application/x-www-form-urlencoded',
      },
      data: queryString.stringify({
        ...param,
        token: abcAuth.accessToken,
        type,
      }),
    });
    if (res.status !== 200) {
      throw new Error();
    }

    const restData = res.data;
    if (
      restData &&
      restData.message &&
      restData.message.includes('The token has expired.')
    ) {
      const res = await refreshToken();
      const accessToken = res?.accessToken;

      if (
        accessToken !== '' &&
        accessToken !== null &&
        accessToken !== undefined
      ) {
        const res = await axios.request({
          url: `${API_URL}/estimate`,
          method: 'post',
          headers: {
            'content-type': 'application/x-www-form-urlencoded',
          },
          data: queryString.stringify({
            ...param,
            token: accessToken,
            type,
          }),
        });
        if (res.status !== 200) {
          throw new Error();
        }

        const resData = TxResultResponse.parse(res.data);
        return resData.result;
      } else {
        console.log('refreshToken is expired. Re-Login is required!');
        alert('refreshToken is expired. Re-Login is required!');
      }
    } else {
      const resData = TxResultResponse.parse(res.data);
      return resData.result;
    }
  } catch (error: any) {
    throw error?.response?.data;
  }
};
